/**
 * Local, per-workstation registry of connected devices (printers, scanners, etc.).
 *
 * Devices are attached to a specific machine, so they live in localStorage rather
 * than the shared database. The receipt (thermal) printer's share name is also
 * mirrored to the legacy `printer_share_name` key so the existing print flow keeps
 * working unchanged.
 */

export type DeviceType =
  | 'thermal_printer'
  | 'label_printer'
  | 'printer'
  | 'qr_scanner'
  | 'other';

export interface ConnectedDevice {
  id: string;
  /** Human-friendly name shown in lists (e.g. "Front desk receipt printer"). */
  name: string;
  type: DeviceType;
  /** Windows share name — used for printer-type devices. */
  shareName: string;
  /** Connection port/path — used for scanners and serial/USB devices (e.g. COM3). */
  port: string;
  /** Host computer name where a shared printer lives (e.g. RECEPTION-PC). */
  pcName: string;
  /** Host network IP where a shared printer lives (e.g. 192.168.1.50). */
  networkIp: string;
  /**
   * Name of the workstation this device was registered from (the local print
   * agent's hostname). Used when another PC browses the global registry and
   * targets this printer as `\\workstation\shareName`.
   */
  workstation: string;
  /** Whether this device is currently in use. */
  enabled: boolean;
  /** Marks the single device used to print pharmacy counter-sale receipts. */
  isReceiptPrinter: boolean;
  /** Marks the single device used to print specimen (collection) labels. */
  isLabelPrinter: boolean;
  /**
   * Marks the single device used to print diagnostic (lab test) receipts.
   * Optional: when unset, those prints fall back to the Documents printer or the
   * browser print dialog.
   */
  isDiagnosticReceiptPrinter: boolean;
  /**
   * Marks the single device used to print invoices. Optional: when unset, those
   * prints fall back to the Documents printer or the browser print dialog.
   */
  isInvoicePrinter: boolean;
  /**
   * Marks the single device used for regular document (A4/PDF) prints such as
   * invoices and reports. Optional: when unset, those prints fall back to the
   * browser print dialog.
   */
  isDocumentPrinter: boolean;
}

export const DEVICES_STORAGE_KEY = 'connected_devices';

/** Device types that target a shared Windows printer. */
export const PRINTER_DEVICE_TYPES: DeviceType[] = ['thermal_printer', 'label_printer', 'printer'];

export function getDevices(): ConnectedDevice[] {
  try {
    const raw = localStorage.getItem(DEVICES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ConnectedDevice[]) : [];
  } catch {
    return [];
  }
}

export function saveDevices(devices: ConnectedDevice[]): void {
  try {
    localStorage.setItem(DEVICES_STORAGE_KEY, JSON.stringify(devices));
  } catch {
    // localStorage unavailable — ignore.
  }
}

export function createDeviceId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `dev_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Build a device object from a partial form value, filling in defaults.
 */
export function makeDevice(partial: Partial<ConnectedDevice>): ConnectedDevice {
  return {
    id: partial.id || createDeviceId(),
    name: (partial.name || '').trim(),
    type: partial.type || 'other',
    shareName: (partial.shareName || '').trim(),
    port: (partial.port || '').trim(),
    pcName: (partial.pcName || '').trim(),
    networkIp: (partial.networkIp || '').trim(),
    workstation: (partial.workstation || '').trim(),
    enabled: partial.enabled !== false,
    isReceiptPrinter: partial.type === 'thermal_printer' && partial.isReceiptPrinter === true,
    isLabelPrinter:
      PRINTER_DEVICE_TYPES.includes(partial.type || 'other') && partial.isLabelPrinter === true,
    isDiagnosticReceiptPrinter:
      PRINTER_DEVICE_TYPES.includes(partial.type || 'other') &&
      partial.isDiagnosticReceiptPrinter === true,
    isInvoicePrinter:
      PRINTER_DEVICE_TYPES.includes(partial.type || 'other') &&
      partial.isInvoicePrinter === true,
    isDocumentPrinter:
      PRINTER_DEVICE_TYPES.includes(partial.type || 'other') && partial.isDocumentPrinter === true,
  };
}

/** Return the share name of the enabled receipt printer (if any). */
export function getReceiptPrinter(): ConnectedDevice | undefined {
  return getDevices().find((d) => d.isReceiptPrinter && d.enabled);
}

/** Return the enabled device designated to print specimen (collection) labels. */
export function getLabelPrinter(): ConnectedDevice | undefined {
  return getDevices().find((d) => d.isLabelPrinter && d.enabled);
}

/**
 * Return the enabled device designated for diagnostic (lab test) receipts.
 * Undefined means the receipts page uses the Documents printer or browser dialog.
 */
export function getDiagnosticReceiptPrinter(): ConnectedDevice | undefined {
  return getDevices().find((d) => d.isDiagnosticReceiptPrinter && d.enabled);
}

/**
 * Return the enabled device designated for invoices. Undefined means invoice
 * prints use the Documents printer or the browser dialog.
 */
export function getInvoicePrinter(): ConnectedDevice | undefined {
  return getDevices().find((d) => d.isInvoicePrinter && d.enabled);
}

/**
 * Return the enabled device designated for regular document (A4/PDF) prints,
 * e.g. invoices and reports. Undefined means those prints use the browser print
 * dialog.
 */
export function getDocumentPrinter(): ConnectedDevice | undefined {
  return getDevices().find((d) => d.isDocumentPrinter && d.enabled);
}

/**
 * Host (PC name / IP) that owns a device's shared printer. Empty means the
 * printer lives on this machine. This is the single place the target host is
 * derived so the Devices page, print routing and receipt/label printing all
 * agree on where a job is sent.
 */
export function getDeviceHost(
  device: Pick<ConnectedDevice, 'networkIp' | 'pcName' | 'workstation'> | null | undefined,
): string {
  if (!device) return '';
  return (device.networkIp || device.pcName || device.workstation || '').trim();
}

/**
 * True when `host` names a machine other than this one, so a print must be
 * delivered through the cloud relay rather than this PC's print agent. Loopback,
 * empty, this PC's own agent hostname and any of its IPv4 addresses are treated
 * as local.
 */
export function isRemoteHost(
  host: string,
  localHostname = '',
  localIps: string[] = [],
): boolean {
  const h = (host || '').trim().toLowerCase();
  if (!h || h === 'localhost' || h === '127.0.0.1' || h === '::1' || h === '.') return false;
  if (localHostname && h === localHostname.trim().toLowerCase()) return false;
  if (localIps.some((ip) => (ip || '').trim().toLowerCase() === h)) return false;
  return true;
}
